import { parsePhoneNumber } from 'libphonenumber-js';
import throttle from 'lodash/throttle';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import { useInitiateMyEmailReminderMutation } from 'apollo/queries/autogenerate/hooks-public';
import {
  PinCodeDispatchMethod,
  Platform as ReCaptchaPlatform,
} from 'apollo/queries/autogenerate/schemas-public';
import { ACTION, ITEM, SCREEN } from 'shared/modules/auth/constants/mixpanel';
import Button from 'shared/modules/common/components/atoms/Button';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import generateID from 'shared/modules/common/utils/generate-id';
import validator from 'shared/utils/validator';
import Mixpanel from 'shared/vendor/Mixpanel';

const EDIT_FIELD_EVENT = `${SCREEN.FORGOT_EMAIL_ADDRESS} - ${ACTION.FIELD_EDIT}`;

type Props = {
  getRecaptchaTokenFunction: () => Promise<string>;
  reCaptchaPlatform: ReCaptchaPlatform;
  onError: () => void;
  onSuccess: (phone: string, otpSession: string) => void;
  clearState: boolean;
};

type FormProps = {
  phone: string;
  otp: string;
};

const VIEW_FORGOT_EMAIL_MULTIPLE_ATTEMPTS_ERROR = `${SCREEN.FORGOT_EMAIL_MULTIPLE_ATTEMPTS_ERROR} - ${ACTION.PAGE_VIEW}`;
const CLICK_EVENT = `${SCREEN.FORGOT_EMAIL_ADDRESS} - ${ACTION.CLICK}`;

const EmailReminderInputPhone = (props: Props) => {
  const form = useForm<FormProps>({
    defaultValues: {
      phone: '',
    },
  });

  form.watch();

  // Reference:
  // https://github.com/paidy/api-consumers-rust/blob/master/docs/flows/emailReminder/initiate.md
  const [initiateMyEmailReminder] = useInitiateMyEmailReminderMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (props.clearState) {
      form.setValue('phone', '');
      form.clearError();
    }
  }, [props.clearState]);

  useEffect(() => {
    form.register(
      {
        name: 'phone',
      },
      {
        validate: validator.getRules([
          'mustNotBeEmptyOrSpace',
          'mustBeValidPhone',
        ]),
      }
    );
  }, []);

  const submitInitiateEmailReminder = throttle(
    async (method: PinCodeDispatchMethod) => {
      Mixpanel.trackEvent(CLICK_EVENT, {
        ITEM: ITEM.SUBMIT,
        'Event Type': method,
      });

      const isValid = await form.triggerValidation(['phone']);

      if (isValid) {
        try {
          const reCaptchaToken = await props.getRecaptchaTokenFunction();
          const { phone } = form.getValues();

          setErrorMessage('');

          try {
            // API requires international standard format
            // e.g. 08000000001 -> +8000000001
            const phoneWithCountryPrefix = parsePhoneNumber(
              phone,
              'JP'
            ).number.toString();
            const result = await initiateMyEmailReminder({
              variables: {
                phoneNumber: phoneWithCountryPrefix,
                pinCodeDispatchMethod: method,
                recaptchaPlatform: props.reCaptchaPlatform,
                recaptchaToken: reCaptchaToken,
              },
            });

            // Expect to get a session value
            if (result.data) {
              props.onSuccess(phone, result.data?.initiateMyEmailReminder);
            } else {
              setErrorMessage('エラーが発生しました');
            }
            // Failed to initialize reminder flow
          } catch (e: any) {
            // BE also validate phone number format.
            if (e.graphQLErrors?.[0]?.message === 'wrong phone_number') {
              setErrorMessage(
                '携帯電話番号に誤りがあります。11桁で入力してください。'
              );
              // This phone number has already been used in the email reminder flow within 24h
            } else if (e.graphQLErrors?.[0]?.extensions?.code === 403) {
              Mixpanel.trackEvent(VIEW_FORGOT_EMAIL_MULTIPLE_ATTEMPTS_ERROR, {
                ITEM: ITEM.CLOSE_DIALOG,
              });

              props.onError();

              // Usually something wrong with BE
            } else if (e.graphQLErrors?.[0]?.extensions?.code === 500) {
              setErrorMessage('サーバーエラーが発生しました');
            } else {
              setErrorMessage('エラーが発生しました');
            }
          }
          // Failed to get a recaptcha token.
        } catch (e) {
          setErrorMessage('エラーが発生しました');
        }
      } else {
        setErrorMessage('入力内容を確認してください');
      }
    },
    2000,
    { leading: true, trailing: false }
  );

  return (
    <View
      style={style.container}
      {...generateID('step1-email-reminder-dialog')}
    >
      <View style={style.topSection}>
        <Text style={style.body}>
          携帯電話番号を入力して、認証コードを受け取ってください。認証コード入力後、ご登録のメールアドレスがSMSに送信されます。
        </Text>
        <Text style={style.caption}>
          ※セキュリティ上の理由により、メールアドレスは一部を隠した状態でお送りします。
        </Text>
        <TextInput
          label="携帯電話番号"
          placeholder="090 0000 0000"
          value={form.getValues('phone')}
          onChangeText={(text) => form.setValue('phone', text)}
          onFocus={() => {
            Mixpanel.trackEvent(EDIT_FIELD_EVENT, {
              Item: ITEM.ENTER_PHONE_NUMBER,
            });
          }}
          onBlur={() => {
            setErrorMessage('');
            form.clearError('phone');
            form.triggerValidation('phone');
          }}
          message={(form.errors.phone?.message as string) || errorMessage || ''}
          state={
            form.errors.phone?.message || errorMessage ? 'error' : undefined
          }
          id="edit-phone-field"
        />
      </View>
      <Button
        title="認証コードを受け取る"
        onPress={() => {
          submitInitiateEmailReminder(PinCodeDispatchMethod.SMS);
        }}
        style={style.button}
        id="edit-phone-next-button"
      />
    </View>
  );
};

const style = StyleSheet.create({
  container: {
    flex: 1,
  },
  topSection: {
    flexGrow: 1,
  },
  body: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '300',
    color: '#2a2e32',
    marginBottom: 24,
  },
  caption: {
    fontSize: 13,
    lineHeight: 16,
    fontWeight: '300',
    color: '#586065',
    marginBottom: 32,
  },
  button: {
    maxWidth: 296,
    alignSelf: 'center',
    marginVertical: 8,
  },
});

export default EmailReminderInputPhone;
